// Types for session reminders (EPIC E7). Mirrors the BE module at
// kayfa-be/src/modules/v1/reminders, mounted at /v1/reminders. A "delivery" is
// one row of the idempotency ledger: one class occurrence × one learner × one
// offset × one channel. The ledger is append-only — the admin surface reads it
// and may trigger exactly one audited resend per row; nothing here edits or
// deletes a row.
//
// §9 of the epic spec is FROZEN: every field name below is the wire name. Do
// not rename, do not add, do not "improve" — three repos code against it.
// Responses ARE { data }-wrapped: read res.data?.data ?? res.data.

// ----------------------------------------------------------------------
// Enumerations. The BE may widen any of these without a FE release, so the
// arrays below drive the filter dropdowns only — every renderer must fall back
// to printing an unrecognised value verbatim rather than crash.

// The five STORED states (§6.1).
export const REMINDER_STATES = [
  'claimed',
  'sending',
  'sent',
  'failed',
  'suppressed',
] as const;
export type ReminderState = typeof REMINDER_STATES[number];

// The two values the audience grid COMPUTES for a cell that has no ledger row
// yet (§9.5). They are never persisted and never appear in /admin/deliveries.
export const REMINDER_COMPUTED_STATES = ['planned', 'would-suppress'] as const;
export type ReminderCellState =
  | ReminderState
  | typeof REMINDER_COMPUTED_STATES[number];

export const REMINDER_CHANNELS = ['inapp', 'email', 'whatsapp'] as const;
export type ReminderChannel = typeof REMINDER_CHANNELS[number];

// Minutes before the class starts. Positive values are reminders; the three
// negative sentinels are notices and the operator resend, and they can never
// collide with a real offset.
export const REMINDER_OFFSETS = [1440, 60, 10] as const;
export const OFFSET_CANCELLED = -1;
export const OFFSET_MOVED = -2;
export const OFFSET_MANUAL = -10;

// The closed reason vocabulary (§6.4), in the spec's own grouping order.
export const REMINDER_REASONS = [
  // alertable
  'window-lapsed',
  'abandoned',
  'unknown-outcome',
  // backfill rule (expected, never alertable)
  'created-after-window',
  'enrolled-after-window',
  'rescheduled-after-window',
  // schedule change
  'cancelled',
  'rescheduled',
  'minor-reschedule',
  'session-missing',
  'superseded',
  'too-late',
  // learner choice / policy
  'opted-out',
  'quiet-hours',
  // data quality / config (ops action)
  'no-email',
  'no-phone',
  'invalid-phone',
  'channel-unconfigured',
  'channel-disabled',
  // provider
  'provider-rejected',
  'provider-error',
  // in-app dedupe
  'idempotent-replay',
  // operator pauses (never alertable)
  'batch-paused',
  'sends-paused',
] as const;
export type ReminderReason = typeof REMINDER_REASONS[number];

// The reasons `problems=true` collapses to — the ops worklist. Mirrors the BE
// shortcut exactly so the checkbox and the reason dropdown agree.
export const REMINDER_PROBLEM_REASONS: string[] = [
  'window-lapsed',
  'abandoned',
  'unknown-outcome',
  'invalid-phone',
  'no-phone',
  'no-email',
  'channel-unconfigured',
  'provider-rejected',
  'provider-error',
];

// The three reasons the runbook pages/tickets on (§10).
export const REMINDER_ALERT_REASONS: string[] = [
  'window-lapsed',
  'abandoned',
  'unknown-outcome',
];

// ----------------------------------------------------------------------
// §9.3 GET /reminders/admin/deliveries

// One ledger row on the wire. `userEmail` and `destination` arrive ALREADY
// MASKED — there is no unmasked form and the dashboard must not try to build
// one. `classTimeLocal` is the class start pre-rendered by the BE in this row's
// stored timezone + locale; display it verbatim, never reformat it client-side,
// or the dashboard and the learner's reminder can disagree.
export type ReminderDeliveryRow = {
  id: string;
  diplomaId: string;
  diplomaName: string;
  diplomaNameAr: string;
  batchName: string;
  // May be null: a schedule regenerate strands the link. The row is still valid.
  sessionId: string | null;
  sessionOrder: number;
  sessionTitle: string;
  scheduledAtMs: number;
  scheduledAt: string;
  fireAt: string;
  offsetMinutes: number;
  channel: string;
  state: string;
  reason: string | null;
  userId: string;
  userName: string;
  userEmail: string;
  destination: string;
  timezone: string;
  locale: string;
  classTimeLocal: string;
  attempts: number;
  sentAt: string | null;
  lastError: string | null;
  manualResendOf: string | null;
  resentBy: string | null;
  createdAt: string;
};

export type ReminderDeliveryList = {
  items: ReminderDeliveryRow[];
  total: number;
  page: number;
  pageSize: number;
};

// ----------------------------------------------------------------------
// §9.4 GET /reminders/admin/summary

export type ReminderStateCounts = {
  claimed: number;
  sending: number;
  sent: number;
  failed: number;
  suppressed: number;
};

export type ReminderSweepHealth = {
  enabled: boolean;
  // null => the sweep is NOT running. Render that as words, never as 0.
  lastSweepAgeSeconds: number | null;
  lastSweepDurationMs: number | null;
  oldestClaimedAgeSeconds: number | null;
  // Whether sends are stopped by EITHER source, and which. Absent on an API
  // that predates the runtime switch — then only `enabled` (the env) is known.
  sendsPause?: ReminderSendsPause;
};

export type ReminderSendsPause = {
  paused: boolean;
  source: 'env' | 'setting' | null;
  setting: {
    paused: boolean;
    pausedAtIso: string | null;
    pausedByEmail: string;
    reason: string;
  };
};

export type ReminderSummary = {
  totals: ReminderStateCounts;
  byChannel: Record<string, ReminderStateCounts>;
  // Keys are STRINGS (JSON object keys), including the negative sentinels.
  byOffset: Record<string, ReminderStateCounts>;
  // Sorted by count descending; only non-zero reasons are present.
  byReason: { reason: string; count: number }[];
  alerts: { windowLapsed: number; abandoned: number; unknownOutcome: number };
  sweep: ReminderSweepHealth;
};

// ----------------------------------------------------------------------
// §9.5 GET /reminders/admin/sessions/:sessionId/audience

export type ReminderAudienceSession = {
  id: string;
  diplomaId: string;
  diplomaName: string;
  diplomaNameAr: string;
  batchName: string;
  title: string;
  order: number;
  scheduledAt: string;
  scheduledAtMs: number;
  active: boolean;
  // false => the orphan gate fires and NOTHING will be sent for this session.
  batchExists: boolean;
  batchTimezone: string;
  createdAt: string;
  scheduledAtChangedAt: string | null;
};

export type ReminderAudienceCell = {
  offsetMinutes: number;
  channel: string;
  state: string; // ReminderCellState, widened for forward compatibility
  reason: string | null;
  at: string | null;
  deliveryId: string | null;
};

export type ReminderAudienceItem = {
  user: { id: string; name: string; email: string; phone: string };
  timezone: string;
  timezoneSource: string; // 'user' | 'batch' | 'default'
  locale: string;
  prefs: { email: boolean; whatsapp: boolean; inapp: boolean };
  classTimeLocal: string;
  cells: ReminderAudienceCell[];
};

export type ReminderAudience = {
  session: ReminderAudienceSession;
  // offset (as a string key) -> the channels that offset sends on.
  matrix: Record<string, string[]>;
  generatedAt: string;
  totals: {
    students: number;
    sent: number;
    failed: number;
    suppressed: number;
    claimed: number;
    sending: number;
    planned: number;
    wouldSuppress: number;
  };
  items: ReminderAudienceItem[];
};

// ----------------------------------------------------------------------
// §9.6 POST /reminders/admin/deliveries/:id/resend
//
// `resent` is true whenever the attempt was MADE — `state` reports the outcome
// honestly and may be 'failed' with a reason. A 409 is a normal, expected
// outcome (the once-ever rule is a unique index, not a UI convention).

export type ReminderResendResult = {
  resent: boolean;
  deliveryId: string;
  sourceDeliveryId: string;
  channel: string;
  state: string;
  reason: string | null;
};
